import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingDown, TrendingUp, Minus, AlertCircle } from "lucide-react";
import type { KpiTrendData } from "@/hooks/useKpiTrend";
import { formatCost } from "./pricing";

const DEFAULT_TARGET = 18;
const CHART_HEIGHT = 200;

function TrendIcon({ trend }: { trend: string }) {
  if (trend === "improving") return <TrendingDown className="h-4 w-4 text-green-600" />;
  if (trend === "degrading") return <TrendingUp className="h-4 w-4 text-red-600" />;
  return <Minus className="h-4 w-4 text-gray-400" />;
}

function TrendLabel({ trend }: { trend: string }) {
  const labels: Record<string, string> = {
    improving: "mejorando",
    degrading: "empeorando",
    stable: "estable",
  };
  return <span>{labels[trend] ?? trend}</span>;
}

interface EfficiencyTrendChartProps {
  data: KpiTrendData;
  target?: number;
}

export function EfficiencyTrendChart({
  data,
  target = DEFAULT_TARGET,
}: EfficiencyTrendChartProps) {
  if (data.weeks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
        <TrendingUp className="h-10 w-10 text-muted-foreground/40 mb-4" />
        <h3 className="text-sm font-medium text-foreground mb-1">No efficiency data yet</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          Complete a few stories to start tracking cost trends.
        </p>
      </div>
    );
  }

  const chartData = data.weeks.map((w) => ({
    week: new Date(w.week).toLocaleDateString("es", { month: "short", day: "numeric" }),
    avg: w.avg_cost,
    target: w.target,
    story_count: w.story_count,
  }));

  const overheadPct = Math.round(data.overhead_ratio * 100);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1">
          <TrendIcon trend={data.trend} />
          <TrendLabel trend={data.trend} />
        </div>
        {data.current_avg !== null && (
          <span className="text-muted-foreground">
            Actual: <strong>{formatCost(data.current_avg)}/story</strong>
          </span>
        )}
        <span
          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
            overheadPct < 15
              ? "bg-green-100 text-green-800"
              : overheadPct < 30
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
          }`}
        >
          overhead {overheadPct}%
        </span>
      </div>

      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="efficiencyGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(222.2 47.4% 11.2%)" stopOpacity={0.15} />
              <stop offset="95%" stopColor="hsl(222.2 47.4% 11.2%)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 20.2% 90%)" />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} unit="$" />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const entry = payload[0]?.payload as { avg: number; story_count: number } | undefined;
              return (
                <div className="rounded border bg-background px-3 py-2 text-xs shadow-md">
                  <p className="font-medium">{label}</p>
                  <p>Avg: {formatCost(entry?.avg ?? 0)}/story</p>
                  <p className="text-muted-foreground">{entry?.story_count ?? 0} stories</p>
                </div>
              );
            }}
          />
          <ReferenceLine
            y={target}
            stroke="hsl(0 72% 51%)"
            strokeDasharray="4 2"
            label={{ value: `$${target} target`, fontSize: 10, fill: "hsl(0 72% 51%)" }}
          />
          <Area
            type="monotone"
            dataKey="avg"
            stroke="hsl(222.2 47.4% 11.2%)"
            fill="url(#efficiencyGradient)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>

      {data.outliers.length > 0 && (
        <div className="space-y-1">
          <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            <AlertCircle className="h-3 w-3" />
            Outliers (&gt;P95)
          </p>
          {data.outliers.map((o) => (
            <div key={o.work_item_ref} className="flex items-center justify-between text-xs">
              <span className="font-mono">{o.work_item_ref}</span>
              <span className="text-red-600">{formatCost(o.cost_usd)}</span>
              {o.dominant_phase && (
                <span className="text-muted-foreground">{o.dominant_phase}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
